// src/pages/SmartFarmingDashboard.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import '../styles/SmartFarmingDashboard.css';
import { FaWifi, FaChevronDown, FaSpinner } from 'react-icons/fa';

const SmartFarmingDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sensorData, setSensorData] = useState([]);
    const [actuatorData, setActuatorData] = useState([]);
    const [systemStatus, setSystemStatus] = useState("Memuat...");
    const pollingIntervalRef = useRef(null);
    const token = localStorage.getItem('token');
    const currentUser = localStorage.getItem('username');
    const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

    // ✅ State baru untuk Super Admin
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(currentUser);
    const [usersLoading, setUsersLoading] = useState(false);

    // ✅ Fetch daftar semua akun (khusus untuk Super Admin)
    const fetchAllUsers = useCallback(async () => {
        if (!isSuperAdmin) return;
        setUsersLoading(true);
        try {
            const response = await axios.get('https://depotaircerdas.com/api/superadmin/tables/accounts?_limit=100', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAllUsers(response.data.map(user => user.username) || []);
        } catch (err) {
            console.error("Gagal mengambil daftar pengguna:", err);
        } finally {
            setUsersLoading(false);
        }
    }, [isSuperAdmin, token]);

    const fetchDashboardData = useCallback(async () => {
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            setSystemStatus("Memuat...");
            let url = `https://depotaircerdas.com/api/sf/dashboard`;
            
            // ✅ Tambahkan query parameter jika Super Admin memilih user lain
            if (isSuperAdmin && selectedUser && selectedUser !== 'Pilih Pengguna...') {
                 url += `?username=${selectedUser}`;
            }

            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const { current_sensors, actuator_status, user } = response.data.data;
            setSensorData(current_sensors || []);
            setActuatorData(actuator_status || []);
            setSystemStatus("Online");
            setLoading(false);
            setError(null);

        } catch (err) {
            console.error("Gagal mengambil data dashboard:", err.response?.data || err.message);
            setSystemStatus("Offline");
            setError(err.response?.data?.message || err.message || "Gagal mengambil data dashboard.");

            if (err.response?.status === 401 || err.response?.status === 403) {
                localStorage.removeItem('token');
                localStorage.removeItem('username');
                localStorage.removeItem('is_super_admin');
                navigate('/login');
            }
            setSensorData([]);
            setActuatorData([]);
        }
    }, [token, navigate, isSuperAdmin, selectedUser]);

    useEffect(() => {
        if (!token) return;

        // Panggil fetch all users jika Super Admin
        if (isSuperAdmin) {
            fetchAllUsers();
        }

        fetchDashboardData();

        pollingIntervalRef.current = setInterval(() => {
            fetchDashboardData();
        }, 10000);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [fetchDashboardData, fetchAllUsers, isSuperAdmin, token]);

    // ✅ Handler untuk mengganti user yang dipilih
    const handleUserChange = (e) => {
        const newUser = e.target.value;
        setSelectedUser(newUser);
        setError(null); // Bersihkan error saat ganti user
        setSensorData([]); // Bersihkan data lama
        setActuatorData([]);
    };

    const controlActuator = async (actuatorId, command) => {
        try {
            await axios.post(`https://depotaircerdas.com/api/sf/actuators/${actuatorId}/command`, { command }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            alert(`Perintah '${command}' berhasil dikirim untuk aktuator ${actuatorId}.`);
            fetchDashboardData();
        } catch (err) {
            alert(`Gagal mengirim perintah. ` + (err.response?.data?.message || err.message));
        }
    };


    if (loading && !error) {
        return (
            <div className="sf-dashboard-container sf-loading">
                <img src="https://i.ibb.co/3sH0rW0/logo-alpukat.png" alt="Logo Alpukat" className="logo" />
                <h1 className="title">Smart Farming Alpukat</h1>
                <p>Memuat dashboard...</p>
            </div>
        );
    }

    return (
        <div className="sf-dashboard-container">
            <div className="sf-header">
                <h1 className="sf-title">Dashboard Kebun Alpukat</h1>
                <p>Status Sistem: <span className={`status ${systemStatus === 'Online' ? 'status-online' : 'status-offline'}`}>{systemStatus}</span></p>
                
                {/* ✅ Bagian untuk Super Admin */}
                {isSuperAdmin ? (
                    <div className="user-selector-container">
                        <label htmlFor="user-select">Melihat Data Akun:</label>
                        <select id="user-select" value={selectedUser} onChange={handleUserChange} disabled={usersLoading}>
                            {usersLoading ? (
                                <option value="">Memuat...</option>
                            ) : (
                                <>
                                    <option value="">Pilih Pengguna...</option>
                                    {allUsers.map(user => (
                                        <option key={user} value={user}>{user}</option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>
                ) : (
                    <p>Selamat datang, <span className="welcome-user-text">{currentUser}</span></p>
                )}
            </div>

            {error && <div className="sf-alert-message alert-error">{error}</div>}

            <h2 className="sf-section-title">Data Sensor Terbaru</h2>
            <div className="sf-sensor-grid">
                {sensorData.length === 0 ? (
                    <p className="sf-no-data-message">Tidak ada data sensor terbaru yang ditemukan untuk akun ini.</p>
                ) : (
                    sensorData.map((sensor, index) => (
                        <div key={index} className="sf-card sensor-card">
                            <h4>{sensor.name}</h4>
                            <div className="sf-value">{sensor.value} <span>{sensor.unit}</span></div>
                            <p>Terakhir: {new Date(sensor.timestamp).toLocaleTimeString()}</p>
                        </div>
                    ))
                )}
            </div>

            <h2 className="sf-section-title">Kontrol Aktuator</h2>
            <div className="sf-actuator-section">
                {actuatorData.length === 0 ? (
                     <p className="sf-no-data-message">Tidak ada aktuator terdaftar untuk akun ini.</p>
                ) : (
                    actuatorData.map((actuator, index) => (
                        <div key={index} className="sf-card actuator-card">
                            <h4>{actuator.name}</h4>
                            <div className="actuator-status-display">
                                Status: <span className={`status ${actuator.status ? 'status-online' : 'status-offline'}`}>
                                    {actuator.status ? 'AKTIF' : 'NONAKTIF'}
                                </span>
                            </div>
                            <div className="actuator-actions">
                                <button onClick={() => controlActuator(actuator.id, 'TURN_ON')} className="btn-actuator btn-turn-on">Aktifkan Manual</button>
                                <button onClick={() => controlActuator(actuator.id, 'TURN_OFF')} className="btn-actuator btn-turn-off">Nonaktifkan Manual</button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default SmartFarmingDashboard;